import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FileDown, Loader2, XCircle, Shield, Printer } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SharedPDFPage = () => {
  const { shareCode } = useParams();
  const [status, setStatus] = useState('loading');
  const [pdfDataUrl, setPdfDataUrl] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadSharedPDF();
  }, [shareCode]);

  const loadSharedPDF = async () => {
    try {
      setStatus('loading');
      setError(null);
      
      console.log('Loading shared PDF with code:', shareCode);
      const url = `${API_URL}/shared-pdf/${shareCode}`;
      console.log('Fetching from:', url);
      
      const response = await fetch(url);
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        let errorMsg = 'Could not load PDF';
        try {
          const errorData = await response.json();
          errorMsg = errorData.detail || errorMsg;
        } catch (e) {
          errorMsg = `Error ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMsg);
      }
      
      const blob = await response.blob();
      console.log('Blob size:', blob.size);
      const base64 = await blobToBase64(blob);
      setPdfDataUrl(base64);
      setStatus('ready');
      
    } catch (err) {
      console.error('Shared PDF error:', err);
      setError(err.message || 'Could not load shared PDF');
      setStatus('error');
    }
  };

  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const handleDownload = () => {
    if (!pdfDataUrl) return;
    
    const link = document.createElement('a');
    link.href = pdfDataUrl;
    link.download = 'EmergencyCard_Health_Report.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('PDF downloading!');
  };

  const handlePrint = () => {
    if (!pdfDataUrl) return;
    
    const printFrame = document.createElement('iframe');
    printFrame.style.position = 'fixed';
    printFrame.style.right = '0';
    printFrame.style.bottom = '0';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = 'none';
    printFrame.src = pdfDataUrl;
    
    printFrame.onload = () => {
      setTimeout(() => {
        printFrame.contentWindow.focus();
        printFrame.contentWindow.print();
      }, 500);
    };
    
    document.body.appendChild(printFrame);
    setTimeout(() => document.body.removeChild(printFrame), 60000);
    toast.info('Opening print dialog...');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex flex-col">
      {/* Header */}
      <header className="bg-[#0D2340] text-white p-4 shadow-lg">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <Shield className="h-8 w-8" />
          <div>
            <h1 className="text-xl font-bold">EmergencyCard Health</h1>
            <p className="text-xs text-white/70">Shared Health Report</p>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        {status === 'loading' && (
          <Card className="w-full max-w-md">
            <CardContent className="p-8 text-center">
              <Loader2 className="h-12 w-12 mx-auto animate-spin text-[#0D2340]" />
              <h2 className="text-lg font-semibold mt-4">Loading Health Report...</h2>
              <p className="text-slate-500 text-sm mt-2">Please wait</p>
            </CardContent>
          </Card>
        )}

        {status === 'ready' && (
          <div className="w-full max-w-4xl">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Shield className="h-4 w-4 text-green-600" />
                Blockchain Verified
              </div>
              
              <div className="flex gap-2">
                <Button onClick={handlePrint} size="sm" variant="outline">
                  <Printer className="h-4 w-4 mr-2" />
                  Print
                </Button>
                <Button onClick={handleDownload} size="sm">
                  <FileDown className="h-4 w-4 mr-2" />
                  Download
                </Button>
              </div>
            </div>
            
            <Card className="overflow-hidden shadow-xl">
              <iframe
                src={pdfDataUrl}
                className="w-full h-[75vh] border-0"
                title="Shared Health Report"
              />
            </Card>
            
            <p className="text-center text-xs text-slate-400 mt-4">
              This is a time-limited shared view. The link will expire automatically.
            </p>
          </div>
        )}

        {status === 'error' && (
          <Card className="w-full max-w-md">
            <CardContent className="p-8 text-center">
              <XCircle className="h-12 w-12 mx-auto text-red-500" />
              <h2 className="text-lg font-semibold mt-4 text-red-600">Could Not Load Report</h2>
              <p className="text-slate-500 text-sm mt-2">{error}</p>
              <p className="text-slate-400 text-xs mt-4">
                This link may have expired or is invalid.
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Footer */}
      <footer className="text-center py-4 text-xs text-slate-400">
        EmergencyCard Health - Your Digital Health Passport | Blockchain Secured
      </footer>
    </div>
  );
};

export default SharedPDFPage;
